import { useUser } from '@clerk/nextjs'
import type { SubscriptionType } from 'api/dist/shared-types/shared-billing-types'
import { useMemo } from 'react'
import { useBilling } from '@/features/subscription/billing/billing-plans/billing-plan/utils/use-billing'
import { IFeatureFlag } from 'shared'
import posthog from 'posthog-js'
import { IBillingFeatureName } from '@/features/subscription/billing/billing-plans/billing-plan/utils/billing-plan-helper'
import { IAccessRule } from '@/features/access-control/access-control-helpers'
import { getHasBillingFeatureAccess } from '@/features/subscription/utils/billing-helpers'
import { useGetBillingPlansQuery } from '@/api/user/organization/billing/get-billing-plans-query'
import { IEnvironment } from '@/utils/typings/environment'

export type IAccessContext = {
    isSuperUser: boolean | undefined
    subscriptionType?: SubscriptionType
    getIsFeatureFlagEnabled: (flag: IFeatureFlag) => boolean | undefined
    getHasBillingFeatureAccess: (billingFeatureName: IBillingFeatureName) => boolean | undefined
}

/**
 * Hook to evaluate access rules against current user contexts
 * @param rule - The access rule to evaluate against the current user context.
 * @returns True if the user has access, false if the user is denied access, or undefined if the rule is not defined or the necessary data is still loading.
 */
export function useAccessControl(rule: IAccessRule): boolean | undefined {
    const { user, isSignedIn, isLoaded: isUserLoaded } = useUser()

    // Return undefined (loading) in these cases to prevent premature access denial:
    // 1. Clerk hasn't finished loading yet
    // 2. User is signed in but user object is still null (transitional state)
    // 3. User is not yet determined (isSignedIn is undefined)
    const isFullyLoaded = isUserLoaded && isSignedIn !== undefined && (isSignedIn === false || user !== null)

    // Only evaluate super user status when fully loaded
    const isSuperUser = isFullyLoaded && user ? user.publicMetadata?.isSuperUser === true : undefined

    const { currentActiveSubscription } = useBilling()
    const { data: billingPlans } = useGetBillingPlansQuery()

    const context: IAccessContext = useMemo(() => {
        const subscriptionType = currentActiveSubscription?.subscriptionType

        return {
            isSuperUser,
            subscriptionType,
            getIsFeatureFlagEnabled: (flag: IFeatureFlag) => {
                const result = posthog.isFeatureEnabled(flag)

                // With `defaults: '2025-05-24'`, PostHog returns undefined for flags
                // absent from the /decide response (instead of false). Once the decide
                // endpoint has responded, undefined means "not active" — treat as false.
                if (result === undefined && posthog?.['flagsEndpointWasHit']) {
                    return false
                }

                return result
            },
            getHasBillingFeatureAccess: (billingFeatureName) => {
                if (!billingPlans) return undefined

                return getHasBillingFeatureAccess({
                    isSuperUser: isSuperUser ?? false,
                    subscriptionType,
                    feature: billingFeatureName,
                    billingPlans: billingPlans,
                })
            },
        }
    }, [currentActiveSubscription?.subscriptionType, isSuperUser, billingPlans])

    // Fail-safe: deny access if no rule is defined
    if (!rule) {
        console.warn('No access control rule is provided, or necessary data is still loading.')

        return undefined
    }

    return evaluateRule(rule, context)
}

/**
 * Pure function to evaluate an access rule against a context
 */
export function evaluateRule(rule: IAccessRule, context: IAccessContext): boolean | undefined {
    const ruleType = rule.type

    switch (ruleType) {
        case 'superUser':
            // Return undefined if still loading, otherwise return the actual value
            if (context.isSuperUser === undefined) return undefined
            return context.isSuperUser

        case 'featureFlag': {
            return context.getIsFeatureFlagEnabled(rule.flag)
        }

        case 'subscriptionType': {
            if (!context.subscriptionType) return undefined

            if (!rule.allowedTypes.includes(context.subscriptionType)) return false

            return true
        }

        case 'billingFeatureName': {
            return context.getHasBillingFeatureAccess(rule.billingFeatureName)
        }

        case 'environment': {
            if (!rule.environments.includes(process.env.NEXT_PUBLIC_ENVIRONMENT as IEnvironment)) return false

            return true
        }

        case 'and': {
            const results = rule.rules.map((r) => evaluateRule(r, context))
            // If any rule is still loading (undefined), return undefined
            if (results.some((r) => r === undefined)) return undefined

            return results.every((r) => r === true)
        }

        case 'or': {
            const results = rule.rules.map((r) => evaluateRule(r, context))
            // If any rule is true, grant access immediately
            if (results.some((r) => r === true)) return true

            // If any rule is still loading (undefined), return undefined
            if (results.some((r) => r === undefined)) return undefined

            return false
        }

        case 'not': {
            const result = evaluateRule(rule.rule, context)
            // If rule is still loading (undefined), return undefined
            if (result === undefined) return undefined

            return !result
        }

        case 'allow':
            return true

        case 'deny':
            return false

        default:
            ruleType satisfies never

            return false
    }
}
